/**
 * water_physics — the PURE rules of interactive water (2026-09-11, "make water
 * more interactable … realistic water physics … affected by wind and the
 * environment"). No three.js, no DOM: the harness pins every number.
 *
 *   SEA STATE    the wind sets the swell — an amplitude scale over the authored
 *                wave set, the whitecap threshold, the detail-ripple drift and
 *                a slow turn of every wave heading toward the wind. Pinned so
 *                the TYPICAL wind (0.45) reproduces the authored look exactly
 *                (the visual-quality contract: the toggle changes nothing at
 *                rest) — a calm flattens it, a gale raises it, a storm on top.
 *                Amplitudes and steepness grow together (a fixed Qᵢ), so the
 *                scale stays under 1/steepness (0.5 → 2.0) with margin.
 *   SLEW         wave parameters never jump (the RDR2 "fronts build, never
 *                flip" rule): amplitude at 0.08/s, heading at 2°/s.
 *   STAMPS       every disturbance is a gaussian on the interaction field —
 *                strides (wading), swim strokes, a hull's wake, creatures,
 *                a waterfall's plunge, rain. Negative = a depression (a foot,
 *                a hull); the field rings it outward.
 *   SWIMMING     water deeper than SWIM_DEPTH_M over the feet floats the
 *                avatar: a critically damped spring holds the feet
 *                SWIM_FEET_UNDER_M below the surface (chest at the surface),
 *                a stroke (jump) lifts, the crawl moves at SPEED_SWIM_MPS
 *                (combat_core, mirrored server-side). Wading slows with depth.
 *   RIDES        a hull over water follows the surface height and ROCKS on
 *                the surface slope; the wind pushes it downwind.
 *   THE FIELD    the interaction field is a camera-following window, sized
 *                by tier (cells are CPU-priced: ≤ 65k).
 */

import { SPEED_SWIM_MPS } from './combat_core.js';

// ── Sea state ───────────────────────────────────────────────────────────────
/** The wind strength at which the authored wave set is reproduced unchanged. */
export const WIND_TYPICAL = 0.45;
/** Amplitude scale bounds (a fixed steepness normalisation: keep < 2). */
export const AMP_SCALE_MIN = 0.35;
export const AMP_SCALE_MAX = 1.85;
/** The authored whitecap Jacobian threshold (DEFAULT_REALISTIC_WATER). */
export const FOAM_JACOBIAN_TYPICAL = 0.6;
/** Slew limits: amplitude scale per second, heading radians per second (2°/s). */
export const AMP_SLEW_PER_S = 0.08;
export const HEADING_SLEW_RAD_PER_S = (2 * Math.PI) / 180;

export interface SeaState {
  /** Amplitude scale over the authored wave set (1 = authored). */
  ampScale: number;
  /** Jacobian whitecap threshold (higher = more crests break). */
  foamJacobian: number;
  /** Scale on the detail-normal drift speed (1 = authored). */
  driftScale: number;
  /** Foam-trail persistence, seconds. */
  foamPersistence: number;
}

/**
 * The sea state a wind produces. `wind01` is the weather's wind strength,
 * `storm01` the rain/storm intensity (a storm adds swell on top of its wind).
 * At the typical wind every value is the authored one.
 */
export function seaStateFor(wind01: number, storm01 = 0): SeaState {
  const w = clamp01(wind01);
  const st = clamp01(storm01);
  // Significant wave height grows ~U² (Pierson–Moskowitz Hs ≈ 0.22·U²/g;
  // the NOAA Beaufort table) — below the typical wind the sea flattens on a
  // power 1.6 curve, above it the scale climbs to the steepness ceiling at a
  // full gale; a storm adds swell on top.
  const below = Math.pow(w / WIND_TYPICAL, 1.6);
  const above = Math.pow((w - WIND_TYPICAL) / (1 - WIND_TYPICAL), 0.85);
  const ampScale = clamp(
    (w <= WIND_TYPICAL ? AMP_SCALE_MIN + (1 - AMP_SCALE_MIN) * below : 1 + (AMP_SCALE_MAX - 1) * above) + 0.12 * st,
    AMP_SCALE_MIN, AMP_SCALE_MAX,
  );
  // Whitecap coverage rises steeply with the wind (Monahan: W ∝ U^3.4): the
  // Jacobian threshold sits at the authored 0.6 at typical, 0.85 in a gale
  // (more crests break), 0.42 in a calm (almost none).
  const foamJacobian = clamp(
    w <= WIND_TYPICAL ? 0.42 + (FOAM_JACOBIAN_TYPICAL - 0.42) * (w / WIND_TYPICAL) : FOAM_JACOBIAN_TYPICAL + 0.25 * Math.pow(above, 1.5) + 0.03 * st,
    0.4, 0.88,
  );
  const driftScale = 0.75 + 0.55 * (w / WIND_TYPICAL);
  const foamPersistence = 2.5 + 1.5 * clamp01((w - WIND_TYPICAL) / (1 - WIND_TYPICAL));
  return { ampScale, foamJacobian, driftScale, foamPersistence };
}

/**
 * The heading rotation that turns the authored primary wave direction
 * (dir0) onto the wind. Returns radians in (−π, π].
 */
export function windHeadingRotation(dir0X: number, dir0Z: number, windX: number, windZ: number): number {
  if (Math.hypot(windX, windZ) < 1e-6) return 0;
  return wrapAngle(Math.atan2(windZ, windX) - Math.atan2(dir0Z, dir0X));
}

/** Move `cur` toward `target` by at most `rate·dt` (a slew, not a lerp). */
export function slew(cur: number, target: number, rate: number, dt: number): number {
  const step = rate * Math.max(0, dt);
  const d = target - cur;
  return Math.abs(d) <= step ? target : cur + Math.sign(d) * step;
}

/** Slew an angle the SHORT way round. */
export function slewAngle(cur: number, target: number, rate: number, dt: number): number {
  const d = wrapAngle(target - cur);
  const step = rate * Math.max(0, dt);
  return wrapAngle(Math.abs(d) <= step ? target : cur + Math.sign(d) * step);
}

export function wrapAngle(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r <= -Math.PI) r += Math.PI * 2;
  return r;
}

// ── The interaction field ───────────────────────────────────────────────────
export interface FluidField {
  /** Cells per side. */
  res: number;
  /** World extent (metres) of the camera-following window. */
  size: number;
}
/** CPU budget: the wave equation runs every frame over res² cells. */
export const FIELD_CELLS_MAX = 65_536;
/** Ripple propagation speed and damping (RippleField). */
export const RIPPLE_SPEED_MPS = 2.2;
export const RIPPLE_DAMPING = 1.1;

/**
 * The field a tier affords. Every tier gets one (the flag defaults OFF on
 * minimal/potato, but a player who turns it on there gets a small window);
 * the cells are CPU-priced, so the window grows with the tier.
 */
export function fluidFieldFor(tier: string): FluidField {
  switch (tier) {
    case 'minimal': return { res: 96, size: 40 };
    case 'potato': return { res: 96, size: 48 };
    case 'low': return { res: 128, size: 64 };
    case 'med': return { res: 192, size: 96 };
    case 'high': return { res: 256, size: 112 };
    default: return { res: 256, size: 128 }; // ultra
  }
}

// ── Stamps (gaussian disturbances on the field) ─────────────────────────────
export interface Stamp {
  /** Height in metres (negative = a depression). */
  strength: number;
  /** Gaussian radius, metres. */
  radius: number;
}

/** A foot pressing the water at wading depth `depth` (m) at `speed` m/s. */
export function strideStamp(depth: number, speedMps: number): Stamp {
  const d = clamp01(depth / 1.2);
  const v = clamp01(speedMps / 3);
  return { strength: -(0.025 + 0.02 * v) * (0.6 + 0.4 * d), radius: 0.32 + 0.28 * d };
}

/** A swimmer's stroke. */
export function swimStrokeStamp(speedMps: number): Stamp {
  return { strength: -0.035 - 0.015 * clamp01(speedMps / SPEED_SWIM_MPS), radius: 0.65 };
}

/**
 * A hovering hull over water, per fixed tick: its downwash presses a dish
 * even at rest; moving, the dish deepens into a wake the field trails behind.
 */
export function hoverWakeStamp(speedMps: number, hullWidthM: number): Stamp {
  const v = clamp01(speedMps / 8);
  return { strength: -(0.006 + 0.016 * v), radius: 0.9 + 0.35 * hullWidthM };
}

/**
 * A moving hull's wake as the Frostbite/DICE pair (Ottosson): water is RAISED
 * ahead of a body moving horizontally and LOWERED behind it — the bow wave
 * and the trough the field then trails into a V. Returns the two stamps
 * (offset along the velocity in metres) for one fixed tick; at rest only the
 * cushion dish remains.
 */
export function wakeStampPair(speedMps: number, hullWidthM: number, hullLengthM: number): { ahead: Stamp & { along: number }; behind: Stamp & { along: number } } {
  const v = clamp01(speedMps / 8);
  const base = hoverWakeStamp(speedMps, hullWidthM);
  const half = Math.max(0.6, hullLengthM * 0.5);
  return {
    ahead: { strength: -base.strength * 0.55 * v, radius: base.radius * 0.8, along: half },
    behind: { strength: base.strength, radius: base.radius, along: -half * 0.6 },
  };
}

/** A creature of `sizeM` (shoulder height) wading at `speed` m/s. */
export function creatureStamp(sizeM: number, speedMps: number): Stamp {
  const s = clamp(sizeM, 0.3, 6);
  return { strength: -0.02 * Math.sqrt(s) * (0.5 + 0.5 * clamp01(speedMps / 4)), radius: 0.35 * s };
}

/** A waterfall's plunge, per tick: a continuous churn sized by the fall. */
export function plungeStamp(widthM: number, dropM: number): Stamp {
  return { strength: -0.012 * clamp(dropM / 6, 0.4, 2), radius: 0.4 * clamp(widthM, 2, 12) };
}

/** A raindrop. */
export const RAIN_DROP_STAMP: Stamp = { strength: -0.02, radius: 0.16 };
/** Raindrops per m² per second at full density (the field's own cadence, capped). */
export const RAIN_DROPS_PER_M2_S = 0.25;
export const RAIN_DROPS_MAX_PER_S = 480;

/** How many raindrops to stamp this tick over a field of `sizeM`² at `density` (0..1). */
export function rainDropsThisTick(density: number, sizeM: number, dt: number, carry: { debt: number }): number {
  const perS = Math.min(RAIN_DROPS_MAX_PER_S, clamp01(density) * sizeM * sizeM * RAIN_DROPS_PER_M2_S);
  carry.debt += perS * Math.max(0, dt);
  const n = Math.floor(carry.debt);
  carry.debt -= n;
  return n;
}

// ── Swimming ────────────────────────────────────────────────────────────────
/** Water over the feet's ground from which the avatar wades (slows). */
export const WADE_DEPTH_M = 0.35;
/** Water over the feet's ground from which the avatar floats. */
export const SWIM_DEPTH_M = 1.25;
/** Feet held this far under the surface while swimming (the chest at the surface; under SWIM_DEPTH_M so the feet clear the bed at the threshold). */
export const SWIM_FEET_UNDER_M = 1.15;
/** The float spring (critically damped): stiffness 1/s², damping 2√k. */
export const SWIM_SPRING_K = 14;
export const SWIM_SPRING_C = 2 * Math.sqrt(SWIM_SPRING_K);
/** A stroke (the jump key) lifts the swimmer this much, m/s. */
export const SWIM_STROKE_MPS = 2.2;
/** Vertical speed cap while floating (waves, not launches). */
export const SWIM_VY_MAX = 3;
export { SPEED_SWIM_MPS };

export type WaterMode = 'dry' | 'wade' | 'swim';

/** dry / wade / swim from the water surface over the ground under the feet. */
export function waterModeFor(surfaceY: number | null, groundY: number): WaterMode {
  if (surfaceY === null) return 'dry';
  const depth = surfaceY - groundY;
  if (depth < 0.08) return 'dry';
  return depth >= SWIM_DEPTH_M ? 'swim' : 'wade';
}

/** Speed factor while wading: resistance grows with depth (1 → 0.55). */
export function wadeSpeedFactor(depth: number): number {
  return 1 - 0.45 * clamp01((depth - 0.15) / (SWIM_DEPTH_M - 0.15));
}

/**
 * One fixed step of the float: a critically damped spring pulls the feet to
 * `surfaceY − SWIM_FEET_UNDER_M`; a stroke adds lift. Returns the new vy and feet height.
 */
export function swimVerticalStep(vy: number, feetY: number, surfaceY: number, dt: number, stroke: boolean): { vy: number; feetY: number } {
  const target = surfaceY - SWIM_FEET_UNDER_M;
  let v = vy + (SWIM_SPRING_K * (target - feetY) - SWIM_SPRING_C * vy) * dt;
  if (stroke) v = Math.max(v, SWIM_STROKE_MPS);
  v = clamp(v, -SWIM_VY_MAX, SWIM_VY_MAX);
  return { vy: v, feetY: feetY + v * dt };
}

// ── Rides on water ──────────────────────────────────────────────────────────
/** Wind push on a hull over water, m/s² at full wind. */
export const HULL_WIND_ACCEL = 0.9;
/** How much of the surface slope a hull takes as pitch/bank (0..1). */
export const HULL_ROCK_GAIN = 0.7;
/** Rock response, 1/s (a slew toward the surface tilt). */
export const HULL_ROCK_RATE = 2.5;

/**
 * Pitch (about the hull's right axis, nose up positive) and bank (about the
 * forward axis, right side down positive) that lay a hull on a surface of
 * slope (dh/dx, dh/dz) when it faces `yaw` (the vehicle convention: forward
 * = (−sin yaw, −cos yaw)).
 */
export function hullTiltOnSurface(slopeX: number, slopeZ: number, yaw: number): { pitch: number; bank: number } {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const rx = -Math.cos(yaw), rz = Math.sin(yaw);
  const along = slopeX * fx + slopeZ * fz;   // surface rises ahead → nose up
  const across = slopeX * rx + slopeZ * rz;  // surface rises to the right → right side up
  return { pitch: Math.atan(along) * HULL_ROCK_GAIN, bank: -Math.atan(across) * HULL_ROCK_GAIN };
}

/** Wind acceleration on a hull over water (m/s², world XZ). */
export function hullWindAccel(windX: number, windZ: number, wind01: number): { ax: number; az: number } {
  const w = clamp01(wind01);
  return { ax: windX * HULL_WIND_ACCEL * w, az: windZ * HULL_WIND_ACCEL * w };
}

// ── Splashes ────────────────────────────────────────────────────────────────
/** A body of `sizeM` entering the water at `impactMps` (downward): particle count + a ring stamp. */
export function splashFor(impactMps: number, sizeM: number): { count: number; speed: number; stamp: Stamp } {
  const v = clamp(impactMps, 0, 25);
  const s = clamp(sizeM, 0.3, 8);
  const count = Math.round(Math.min(160, (6 + v * 4) * Math.sqrt(s)));
  return { count, speed: 1.5 + v * 0.35, stamp: { strength: -0.04 - 0.01 * v, radius: 0.45 * s } };
}
/** Below this downward speed an entry is a step, not a splash. */
export const SPLASH_MIN_IMPACT_MPS = 2;

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
function clamp01(v: number): number { return clamp(v, 0, 1); }
